import { defineStore } from "pinia";
import _ from "lodash";
import { MfaState, MfaManageStep, MfaStatusEnum, MfaUserDisableRequest, } from "@/shared/types";

import { CognitoUser, IMfaSettings } from "amazon-cognito-identity-js";
import * as storeHelper from "@/shared/storeHelper";
import * as UserService from "@/shared/api/userService";
import { CognitoAuth as Auth } from "@/shared/api/auth";

import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: MfaState = {
    cognitoUser: null,
    step: MfaManageStep.Manage,
    phoneNumber: null,
    totpSecret: null,
    mfaStatus: MfaStatusEnum.Disabled,
    showMfa: false,
};

export const useMfaStore = defineStore("mfa", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("mfa"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        showMfaModal: state => state.showMfa,
    },
    actions: {
        setStep (s: MfaManageStep) {
            this.step = s;
        },
        setPhoneNumber (phoneNumber: string) {
            this.phoneNumber = phoneNumber;
        },
        setCognitoUser (c: CognitoUser) {
            this.cognitoUser = c;
        },
        setTotpSecret (s: string) {
            this.totpSecret = s;
        },
        setMfaStatus (status: MfaStatusEnum) {
            this.mfaStatus = status;
        },
        setShowMfa (value: boolean) {
            this.showMfa = value;
        },
        authenticateUser (data: { email: string, password: string }): Promise<CognitoUser> {
            return new Promise((resolve, reject) => {
                Auth().authenticateCognitoUser(data.email, data.password, (s: MfaStatusEnum, u: CognitoUser) => {
                        this.setCognitoUser(u);
                        this.sendMfaStatus({ status: s });
                        if (s === MfaStatusEnum.Sms || s === MfaStatusEnum.Totp) {
                            this.setStep(MfaManageStep.VerifyMfa);
                        }
                    })
                    .then(u => {
                        this.setCognitoUser(u);
                        resolve(u);
                    }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    reject();
                });
            });
        },
        disableMfa (status: MfaStatusEnum): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                if (status === MfaStatusEnum.Disabled) {
                    return resolve();
                }
                // must set the correct options to be passed based on their current choice, otherwise if they
                // are not configured it will throw an error, e.g. disable totp (false,false) when they don't have
                // totp configured will throw an error
                let sms: IMfaSettings = null;
                if (status === MfaStatusEnum.Sms) {
                    sms = {
                        PreferredMfa: false,
                        Enabled: false,
                    };
                }

                let totp: IMfaSettings = null;
                if (status === MfaStatusEnum.Totp) {
                    totp = {
                        PreferredMfa: false,
                        Enabled: false,
                    };
                }

                Auth().setMFAPreference(this.cognitoUser, sms, totp).then(result => {
                    this.sendMfaStatus({ status: MfaStatusEnum.Disabled });
                    this.setMfaStatus(MfaStatusEnum.Disabled);
                    resolve();
                }).catch(err => {
                    reject(err);
                });
            });
        },
        disableMfaWithCode (request: MfaUserDisableRequest): Promise<any> {
            return UserService.disableMfaWithCode(request).then(_ => {
                this.setMfaStatus(MfaStatusEnum.Disabled);
                return Promise.resolve(true);
            }).catch(e => {
                storeHelper.handleError({ error: (e.response.data ? e.response.data : e), rethrow: false });
                return false;
            });
        },
        verifyTotpToken (token): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                Auth().verifyToken(this.cognitoUser, token).then(session => {
                    const totpMfaSettings: IMfaSettings = {
                        PreferredMfa: true,
                        Enabled: true,
                    };

                    const notifierStore = useNotifierStore();
                    Auth().setMFAPreference(this.cognitoUser, null, totpMfaSettings).then(_ => {
                        this.sendMfaStatus({ status: MfaStatusEnum.Totp });
                        this.setMfaStatus(MfaStatusEnum.Totp);
                        this.setMfaPreference(false);
                        this.setStep(MfaManageStep.Manage);
                        notifierStore.setSuccess("TOTP MFA has been added to your account! ");
                        resolve();
                    }).catch(err => {
                        this.setStep(MfaManageStep.Manage);
                        storeHelper.handleError({ error: err, rethrow: false });
                        reject(err);
                    });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    reject(err);
                });
            });
        },
        verifyPhone (phoneVerificationCode): Promise<any> {
            return new Promise<void>((resolve, reject) => {
                Auth().verifyPhone(this.cognitoUser, phoneVerificationCode).then(() => {
                        const smsMfaSettings = {
                            PreferredMfa: true,
                            Enabled: true,
                        };
                        const notifierStore = useNotifierStore();
                        Auth().setMFAPreference(this.cognitoUser, smsMfaSettings, null).then(result => {
                            this.sendMfaStatus({ status: MfaStatusEnum.Sms });
                            this.setMfaStatus(MfaStatusEnum.Sms);
                            this.setMfaPreference(false);
                            this.setStep(MfaManageStep.Manage);
                            notifierStore.setSuccess("SMS MFA has been added to your account!");
                            resolve();
                        }).catch(err => {
                            this.setStep(MfaManageStep.Manage);
                            storeHelper.handleError({ error: err, rethrow: false });
                            reject(err);
                        });
                    })
                    .catch(err => {
                        storeHelper.handleError({ error: err, rethrow: false });
                        reject("Could not verify the phone number. Please check and try again.");
                    });
            });

        },
        updatePhoneNumber (newPhoneNumber): Promise<any> {
            return new Promise((resolve, reject) => {
                // Phone number already set correctly
                if (newPhoneNumber === this.phoneNumber) {
                    Auth().isPhoneVerified(this.cognitoUser).then(verified => {
                        if (verified) {
                            const smsMfaSettings = {
                                PreferredMfa: true,
                                Enabled: true,
                            };

                            const notifierStore = useNotifierStore();
                            Auth().setMFAPreference(this.cognitoUser, smsMfaSettings, null)
                                .then(result => {
                                    this.sendMfaStatus({
                                        status: MfaStatusEnum.Sms,
                                        phoneNumber: newPhoneNumber
                                    });
                                    this.setMfaStatus(MfaStatusEnum.Sms);
                                    this.setMfaPreference(false);
                                    this.setStep(MfaManageStep.Manage);
                                    notifierStore.setSuccess("SMS MFA has been added to your account! ");
                                });
                        } else {
                            Auth().sendPhoneVerification(this.cognitoUser);
                            this.setStep(MfaManageStep.VerifyPhone);
                        }
                    });
                }
                // Phone number not set or verified yet
                else {
                    Auth().updateUserAttribute(this.cognitoUser, "phone_number", newPhoneNumber).then(() => {
                        Auth().sendPhoneVerification(this.cognitoUser);
                        this.setStep(MfaManageStep.VerifyPhone);
                    });
                }
            });
        },
        getTotpSecret (): Promise<any> {
            return Auth().associateToken(this.cognitoUser).then(secretCode => {
                this.setTotpSecret(secretCode);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        loadPhoneNumber (): Promise<any> {
            return Auth().getPhone(this.cognitoUser).then((phone: string) => {
                if (phone) {
                    phone = phone.replace(/^\+01/, "");
                    this.setPhoneNumber(phone);
                }
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        clearState () {
            this.setCognitoUser(null);
            this.setStep(MfaManageStep.Manage);
        },
        updateStep (newStep) {
            this.setStep(newStep);
        },
        sendMfaCode (data: { code: string, isSms: boolean }) {
            this.cognitoUser.sendMFACode(data.code, {
                onSuccess: _ => this.setStep(data.isSms ? MfaManageStep.AddTOTP : MfaManageStep.AddSMS),
                onFailure: _ => this.setStep(MfaManageStep.Authenticate)
            });
        },
        setMfaPreference (enabled: boolean): Promise<boolean> {
            return UserService.setMfaPreference(enabled).then(r => {
                if (r) {
                    this.setShowMfa(enabled);
                    return true;
                } else {
                    storeHelper.handleError({ error: "Could not save MFA preference.", rethrow: false });
                    return false;
                }
            }).catch(e => storeHelper.handleError({ error: e, rethrow: false }));
        },
        sendMfaStatus (params: {
            status: MfaStatusEnum,
            phoneNumber: string
        } | {
            status: MfaStatusEnum,
        } | any): Promise<boolean> {
            const userStore = useUserStore();
            if (userStore.site === "ADMIN") {
                return Promise.resolve(true);
            }

            return UserService.sendMfaStatus(params.status, params.phoneNumber).then(r => {
                if (r) {
                    this.setMfaStatus(params.status);
                    return true;
                } else {
                    storeHelper.handleError({ error: "Could not save MFA Status.", rethrow: false });
                    return false;
                }
            }).catch(e => storeHelper.handleError({ error: e, rethrow: false }));
        },
    }
});